import React, { useState, useMemo, useRef } from 'react';
import MenuDrawer from './MenuDrawer';
import AuthModal from './AuthModal';
import Footer from './Footer';
import BookingDrawer from './BookingDrawer';
import Header from './Header';
import { HeartIcon } from './Icons';
import { triggerStockLimitAlert, resolveMediaUrl, getProductTimestamp } from '../utils/firebaseService';

export default function Marketplace({
  view,
  setView,
  isMenuOpen,
  setIsMenuOpen,
  isCartOpen,
  setIsCartOpen,
  isBookingOpen,
  setIsBookingOpen,
  isSearchOpen,
  setIsSearchOpen,
  favorites,
  setFavorites,
  cartItems,
  setCartItems,
  bookingStep,
  setBookingStep,
  bookingForm,
  setBookingForm,
  products,
  setLegalTitle,
  setSelectedCollectionId,
  setSelectedProduct,
  selectedCategory,
  setSelectedCategory,
  currentUser,
  setCurrentUser,
  wishlist = [],
  toggleWishlist,
}) {
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const activeCategory = selectedCategory || 'All';
  const setActiveCategory = setSelectedCategory;
  React.useEffect(() => {
    console.log("Marketplace mounted/updated selectedCategory:", selectedCategory);
  }, [selectedCategory]);
  const [sortBy, setSortBy] = useState('default'); // 'price-asc', 'price-desc', 'discount-desc', 'default'
  const [addedProductIds, setAddedProductIds] = useState({});
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Cart management utilities
  const [undoItem, setUndoItem] = useState(null);
  const undoTimerRef = useRef(null);

  const handleQtyChange = (id, delta) => {
    setCartItems(prev => {
      const item = prev.find(i => String(i.id) === String(id));
      if (!item) return prev;
      if (item.qty + delta <= 0) {
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setUndoItem(item);
        undoTimerRef.current = setTimeout(() => setUndoItem(null), 5000);
        return prev.filter(i => String(i.id) !== String(id));
      }
      const liveProd = (products || []).find(p => String(p.id) === String(id));
      const itemStock = (liveProd && typeof liveProd.stock === 'number')
        ? liveProd.stock
        : ((typeof item.stock === 'number') ? item.stock : 10);
      if (delta > 0 && item.qty + delta > itemStock) {
        triggerStockLimitAlert(itemStock);
        return prev.map(i => String(i.id) === String(id) ? { ...i, stock: itemStock, qty: itemStock } : i);
      }
      return prev.map(i => String(i.id) === String(id) ? { ...i, stock: itemStock, qty: i.qty + delta } : i);
    });
  };

  const removeItem = (id) => {
    const item = cartItems.find(i => String(i.id) === String(id));
    if (item) {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      setUndoItem(item);
      undoTimerRef.current = setTimeout(() => setUndoItem(null), 5000);
    }
    setCartItems(prev => prev.filter(i => String(i.id) !== String(id)));
  };

  const handleUndo = () => {
    if (undoItem) {
      setCartItems(prev => [...prev, { ...undoItem, qty: 1 }]);
      setUndoItem(null);
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    }
  };

  const getSubtotal = () => {
    return cartItems.reduce((acc, item) => acc + (item.price * item.qty), 0);
  };

  const categories = [
    'All',
    'Best seller',
    'New arrival',
    'Rings',
    'Earrings',
    'Necklaces',
    'Pendants',
    'Bracelets',
    'Bangles',
    'Maangtika',
    'Nath',
    'Brooch',
    'Baajubandh',
    'Sets'
  ];

  // Filter and sort products based on selected parameters
  const processedProducts = useMemo(() => {
    let result = [...products];

    // Filter by Category
    if (activeCategory !== 'All') {
      if (activeCategory === 'Best seller') {
        result = result.filter(p => p.isBestSeller);
      } else if (activeCategory === 'New arrival') {
        result = result.filter(p => p.isNewArrival || p.category === 'New arrival');
      } else {
        result = result.filter(p => p.category?.toLowerCase() === activeCategory.toLowerCase());
      }
    }

    // Sort by Parameters
    if (sortBy === 'price-asc') {
      result.sort((a, b) => {
        const pA = a.price - (a.price * (a.discount || 0)) / 100;
        const pB = b.price - (b.price * (b.discount || 0)) / 100;
        return pA - pB;
      });
    } else if (sortBy === 'price-desc') {
      result.sort((a, b) => {
        const pA = a.price - (a.price * (a.discount || 0)) / 100;
        const pB = b.price - (b.price * (b.discount || 0)) / 100;
        return pB - pA;
      });
    } else if (sortBy === 'discount-desc') {
      result.sort((a, b) => (b.discount || 0) - (a.discount || 0));
    } else {
      // Default: newest first by timestamp so newly added products are at the top
      result.sort((a, b) => getProductTimestamp(b) - getProductTimestamp(a));
    }

    return result;
  }, [products, activeCategory, sortBy]);

  const handleAddToCart = (product) => {
    const liveProd = (products || []).find(p => String(p.id) === String(product.id));
    const maxStock = (liveProd && typeof liveProd.stock === 'number')
      ? liveProd.stock
      : (typeof product.stock === 'number' ? product.stock : 10);

    if (maxStock <= 0) {
      triggerStockLimitAlert(0);
      return;
    }

    let limitReached = false;

    setCartItems(prev => {
      const existing = prev.find(item => String(item.id) === String(product.id));
      const currentQty = existing ? existing.qty : 0;
      if (currentQty + 1 > maxStock) {
        limitReached = true;
        triggerStockLimitAlert(maxStock);
        if (existing) {
          return prev.map(item => String(item.id) === String(product.id) ? { ...item, stock: maxStock, qty: maxStock } : item);
        } else {
          return prev;
        }
      }
      if (existing) {
        return prev.map(item => String(item.id) === String(product.id) ? { ...item, stock: maxStock, qty: item.qty + 1 } : item);
      }
      return [...prev, { ...product, stock: maxStock, qty: 1 }];
    });

    if (!limitReached) {
      // Set added checkmark success state
      setAddedProductIds(prev => ({ ...prev, [product.id]: true }));
      setTimeout(() => {
        setAddedProductIds(prev => {
          const next = { ...prev };
          delete next[product.id];
          return next;
        });
      }, 2000);
    }
  };

  return (
    <div className="bg-noise min-h-screen relative font-sans text-[#584738] flex flex-col antialiased">
      
      {/* STICKY HEADER */}
      <Header
        setView={setView}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        setIsSearchOpen={setIsSearchOpen}
        setIsAuthOpen={setIsAuthOpen}
        setIsCartOpen={setIsCartOpen}
        cartItems={cartItems}
      />



      {/* MOBILE FILTER & SORT HEADER CONTROLS */}
      <div className="block md:hidden sticky top-[58px] sm:top-[68px] z-30 bg-[#FAF8F5]/95 backdrop-blur-md border-b border-[#584738]/10 px-3.5 sm:px-6 py-2.5 sm:py-3.5 shadow-xs">
        <div className="flex gap-2.5">
          <button
            onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
            className="flex-1 bg-white border border-[#584738]/20 text-[10.5px] sm:text-[11px] font-sans font-medium text-[#584738] tracking-wider py-2 sm:py-2.5 px-3 uppercase rounded-xs flex items-center justify-between cursor-pointer active:scale-98"
          >
            <span className="truncate">Filter: {activeCategory}</span>
            <svg className={`w-3.5 h-3.5 shrink-0 transition-transform duration-300 ${mobileFiltersOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </button>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="flex-1 bg-white border border-[#584738]/20 text-[10.5px] sm:text-[11px] font-sans font-medium text-[#584738] tracking-wider py-2 sm:py-2.5 px-2.5 uppercase rounded-xs focus:outline-none cursor-pointer"
          >
            <option value="default">Sort: Default</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
            <option value="discount-desc">Highest Discount</option>
          </select>
        </div>

        {/* Mobile Fast Category Chips Strip */}
        <div className="flex overflow-x-auto no-scrollbar gap-1.5 pt-2 pb-0.5 -mx-1 px-1">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`whitespace-nowrap px-3 py-1 text-[10px] uppercase tracking-wider font-sans rounded-full transition-all shrink-0 cursor-pointer ${
                activeCategory === cat
                  ? 'bg-[#584738] text-white font-semibold shadow-xs'
                  : 'bg-white border border-[#584738]/15 text-[#584738]/70 hover:border-[#584738]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {mobileFiltersOpen && (
          <div className="mt-2.5 bg-white border border-[#584738]/10 rounded-xs p-3.5 flex flex-col gap-1.5 max-h-56 overflow-y-auto scrollbar-thin shadow-md">
            <p className="text-[9.5px] tracking-widest font-bold text-[#584738]/50 uppercase mb-1">Select Category</p>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setActiveCategory(cat);
                  setMobileFiltersOpen(false);
                }}
                className={`text-left py-1.5 px-1 text-xs tracking-wider uppercase font-sans cursor-pointer ${
                  activeCategory === cat ? 'text-[#584738] font-bold bg-[#584738]/5' : 'text-[#584738]/70 font-light'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* CORE MARKETPLACE GRID SECTION */}
      <div className="max-w-7xl mx-auto px-3.5 sm:px-6 md:px-12 py-6 sm:py-10 md:py-16 w-full flex-1">
        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-12 items-start">
          
          {/* DESKTOP SIDEBAR FILTER CONTROLS */}
          <aside className="hidden md:block sticky top-28 pr-4 border-r border-[#584738]/10">
            {/* Category Filter Group */}
            <h3 className="font-sans text-[11px] tracking-widest text-[#584738]/40 uppercase mb-5 font-bold">
              Filter By Category
            </h3>
            <div className="flex flex-col gap-3.5 mb-10">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`text-left text-xs uppercase tracking-wider font-sans hover:text-[#584738] transition-colors focus:outline-none ${
                    activeCategory === cat ? 'text-[#584738] font-bold border-l-2 border-[#584738] pl-2' : 'text-[#584738]/60 font-light pl-2'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Sorting Filter Group */}
            <h3 className="font-sans text-[11px] tracking-widest text-[#584738]/40 uppercase mb-5 font-bold pt-4 border-t border-[#584738]/10">
              Sort Options
            </h3>
            
            <div className="flex flex-col gap-4">
              <div>
                <p className="text-[10px] tracking-widest text-[#584738]/50 uppercase mb-2 font-semibold">Pricing & Discount</p>
                <div className="flex flex-col gap-2.5">
                  <label className="flex items-center gap-2.5 text-[12.5px] cursor-pointer font-sans font-light hover:text-black">
                    <input
                      type="radio"
                      name="sort-option"
                      checked={sortBy === 'default'}
                      onChange={() => setSortBy('default')}
                      className="accent-[#584738] w-3.5 h-3.5"
                    />
                    <span>Default Showcase</span>
                  </label>
                  <label className="flex items-center gap-2.5 text-[12.5px] cursor-pointer font-sans font-light hover:text-black">
                    <input
                      type="radio"
                      name="sort-option"
                      checked={sortBy === 'price-asc'}
                      onChange={() => setSortBy('price-asc')}
                      className="accent-[#584738] w-3.5 h-3.5"
                    />
                    <span>Price: Low to High</span>
                  </label>
                  <label className="flex items-center gap-2.5 text-[12.5px] cursor-pointer font-sans font-light hover:text-black">
                    <input
                      type="radio"
                      name="sort-option"
                      checked={sortBy === 'price-desc'}
                      onChange={() => setSortBy('price-desc')}
                      className="accent-[#584738] w-3.5 h-3.5"
                    />
                    <span>Price: High to Low</span>
                  </label>
                  <label className="flex items-center gap-2.5 text-[12.5px] cursor-pointer font-sans font-light hover:text-black">
                    <input
                      type="radio"
                      name="sort-option"
                      checked={sortBy === 'discount-desc'}
                      onChange={() => setSortBy('discount-desc')}
                      className="accent-[#584738] w-3.5 h-3.5"
                    />
                    <span>Highest Discount</span>
                  </label>
                </div>
              </div>
            </div>
          </aside>

          {/* MAIN PRODUCT CATALOG GRID */}
          <main>
            {/* Header info */}
            <div className="flex justify-between items-center mb-8 border-b border-[#584738]/10 pb-4">
              <p className="font-sans text-xs text-[#584738]/60 tracking-wider">
                Showing {processedProducts.length} Designs in <span className="font-semibold text-[#584738]">{activeCategory}</span>
              </p>
            </div>

            {processedProducts.length > 0 ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {processedProducts.map((product) => {
                  const hasDiscount = product.discount > 0;
                  const finalPrice = hasDiscount 
                    ? product.price - (product.price * product.discount) / 100
                    : product.price;
                  const currentStock = typeof product.stock === 'number' ? product.stock : 10;
                  const isOutOfStock = currentStock <= 0;
                  const isWishlisted = (wishlist || []).some(id => String(id) === String(product.id));

                  return (
                    <div key={product.id} className="bg-white border border-[#584738]/10 p-2.5 md:p-4 rounded-xs flex flex-col justify-between group hover:shadow-md transition-shadow duration-300 relative">
                      {/* Discount ribbon */}
                      {hasDiscount && (
                        <div className="absolute top-2 left-2 z-10 bg-[#8C3E3E] text-white text-[9px] font-sans font-bold tracking-wider px-2 py-0.5 uppercase shadow-sm">
                          {product.discount}% Off
                        </div>
                      )}

                      {/* Add to Wishlist Icon - Right Side Top */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (toggleWishlist) toggleWishlist(product.id);
                        }}
                        title={isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
                        aria-label={isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
                        className={`absolute top-2.5 right-2.5 z-20 w-8 h-8 rounded-full flex items-center justify-center shadow-xs transition-all duration-300 ease-out active:scale-90 hover:scale-110 cursor-pointer touch-manipulation ${
                          isWishlisted 
                            ? 'bg-rose-50 text-rose-600 border border-rose-200 shadow-sm' 
                            : 'bg-white/90 hover:bg-white text-[#584738]/70 hover:text-[#584738] border border-[#584738]/10'
                        }`}
                      >
                        <HeartIcon
                          filled={isWishlisted}
                          className="w-5 h-5"
                          activeColor="#E11D48"
                        />
                      </button>

                      {/* Out of Stock ribbon */}
                      {isOutOfStock && (
                        <div className={`absolute ${hasDiscount ? 'top-8' : 'top-2'} left-2 z-10 bg-[#584738]/90 text-[#F5E6D8] text-[9px] font-sans font-bold tracking-wider px-2 py-0.5 uppercase shadow-sm`}>
                          Out of Stock
                        </div>
                      )}

                      {/* Product Thumbnail image */}
                      <div 
                        onClick={() => { setSelectedProduct(product); setView('product'); }}
                        className="aspect-square sm:aspect-[4/5] w-full overflow-hidden rounded-xs bg-[#FAF8F5] flex items-center justify-center mb-2.5 md:mb-4 relative cursor-pointer"
                      >
                        <img
                          src={resolveMediaUrl(product.image || product.images?.[0] || '')}
                          alt={product.name}
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            e.target.onerror = null;
                            const altImg = (product.images || []).map(resolveMediaUrl).find(u => u && u !== e.target.src);
                            if (altImg) {
                              e.target.src = altImg;
                            } else {
                              e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="500" viewBox="0 0 400 500"><rect width="400" height="500" fill="%23F4EFEA"/><text x="50%" y="48%" dominant-baseline="middle" text-anchor="middle" font-family="serif" font-size="18" fill="%23584738" letter-spacing="2">HOUSE OF KARNNIKARAA</text><text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="11" fill="%238C7D6E" letter-spacing="1">Handcrafted Heritage</text></svg>';
                            }
                          }}
                          className={`w-full h-full object-cover group-hover:scale-102 transition-transform duration-500 ${isOutOfStock ? 'opacity-75 grayscale' : ''}`}
                        />
                      </div>

                      {/* Title & metadata */}
                      <div className="text-center flex flex-col flex-1 justify-between">
                        <div>
                          <div className="flex items-center justify-center gap-1.5 mb-1 flex-wrap">
                            <span className="text-[8.5px] font-sans tracking-widest font-semibold uppercase bg-[#584738]/5 text-[#584738]/70 px-1.5 py-0.5 rounded-xs">
                              {product.category || 'Jewellery'}
                            </span>
                            {product.isBestSeller && (
                              <span className="text-[8.5px] font-sans tracking-widest font-semibold uppercase bg-[#866529]/15 text-[#866529] px-1.5 py-0.5 rounded-xs">
                                Best Seller
                              </span>
                            )}
                          </div>

                          <h4 
                            onClick={() => { setSelectedProduct(product); setView('product'); }}
                            className="font-serif text-[#584738] text-xs md:text-[15px] font-medium tracking-wide mb-1 min-h-[30px] flex items-center justify-center cursor-pointer hover:underline transition-all duration-200"
                          >
                            {product.name}
                          </h4>
                          
                          <p 
                            className="font-sans text-[9px] md:text-[11px] text-[#584738]/60 mb-2 leading-relaxed max-w-[210px] mx-auto font-light line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: product.description || '' }}
                          />

                          <div className="flex items-center justify-center gap-2 mb-3.5">
                            {hasDiscount ? (
                              <>
                                <span className="font-sans text-[11px] md:text-xs text-[#8C3E3E] font-semibold">
                                  ₹{finalPrice.toLocaleString()}
                                </span>
                                <span className="font-sans text-[9.5px] md:text-[11px] text-[#584738]/40 line-through">
                                  ₹{product.price.toLocaleString()}
                                </span>
                              </>
                            ) : (
                              <span className="font-sans text-[11px] md:text-xs text-[#584738] font-semibold">
                                ₹{product.price.toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Add to Bag trigger */}
                        {isOutOfStock ? (
                          <button
                            disabled={true}
                            className="w-full py-1.5 md:py-2.5 text-[9px] md:text-[10px] tracking-widest font-sans uppercase font-light bg-neutral-300 text-neutral-500 cursor-not-allowed flex items-center justify-center"
                          >
                            Out of Stock
                          </button>
                        ) : (
                          <button
                            onClick={() => handleAddToCart(product)}
                            disabled={addedProductIds[product.id]}
                            className={`w-full py-1.5 md:py-2.5 text-[9px] md:text-[10px] tracking-widest font-sans uppercase font-light transition-all duration-300 flex items-center justify-center gap-1.5 ${
                              addedProductIds[product.id] 
                                ? 'bg-[#3E5C43] text-white cursor-default' 
                                : 'bg-[#584738] text-white hover:bg-black'
                            }`}
                          >
                            {addedProductIds[product.id] ? (
                              <>
                                <svg className="w-3 h-3 stroke-current animate-bounce" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <span>Added</span>
                              </>
                            ) : (
                              <span>Add to Bag</span>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 bg-white border border-[#584738]/10 rounded-xs">
                <p className="font-serif text-lg text-[#584738]/80 mb-2">No items found</p>
                <p className="font-sans text-xs text-[#584738]/50 max-w-xs mx-auto leading-relaxed">
                  We currently do not have designs in the "{activeCategory}" category. Please browse other collections!
                </p>
              </div>
            )}
          </main>

        </div>
      </div>

      {/* FOOTER */}
      <Footer setView={setView} setLegalTitle={setLegalTitle} setIsBookingOpen={setIsBookingOpen} />

      {/* DRAWERS */}
      <MenuDrawer
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        setView={setView}
        setIsBookingOpen={setIsBookingOpen}
        setSelectedCollectionId={setSelectedCollectionId}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
        setIsAuthOpen={setIsAuthOpen}
        wishlist={wishlist}
      />
      <AuthModal isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} currentUser={currentUser} setCurrentUser={setCurrentUser} />

      {/* B. LUXURY CART SLIDE-OVER */}
      <div
        className={`fixed inset-0 z-50 transition-opacity duration-300 pointer-events-none ${
          isCartOpen ? "opacity-100 pointer-events-auto" : "opacity-0"
        }`}
      >
        {/* Backdrop */}
        <div
          onClick={() => setIsCartOpen(false)}
          className="absolute inset-0 bg-black/40 backdrop-blur-xs"
        />

        {/* Cart Drawer */}
        <div
          className={`fixed inset-y-0 right-0 h-full w-full md:w-96 bg-noise shadow-2xl p-6 transition-transform duration-300 flex flex-col ${
            isCartOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between border-b border-[#584738]/10 pb-4 mb-6 flex-shrink-0">
            <h3 className="font-serif text-lg tracking-widest font-semibold uppercase flex items-center gap-2 text-[#584738]">
              Your Cart
              <span className="text-xs bg-[#584738]/10 text-[#584738] px-2.5 py-0.5 rounded-full font-sans font-bold">
                {cartItems.reduce((acc, it) => acc + it.qty, 0)}
              </span>
            </h3>

            <button
              onClick={() => setIsCartOpen(false)}
              className="text-[#584738] hover:text-[#584738] focus:outline-none"
              aria-label="Close Cart"
            >
              <svg className="w-5 h-5 text-[#584738]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1 scrollbar-thin">
            {cartItems.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 text-[#584738]/40 font-serif">
                <svg className="w-8 h-8 opacity-40 mb-3" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1,0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0,1-1.12-1.243l1.264-12A1.125 1.125 0 0,1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1,1-.75 0 .375.375 0 0,1 .75 0Zm7.5 0a.375.375 0 1,1-.75 0 .375.375 0 0,1 .75 0Z" />
                </svg>
                <p className="mt-3 text-sm tracking-wide font-medium">Your cart is empty.</p>
              </div>
            ) : (
              cartItems.map((item) => (
                <div key={item.id} className="flex gap-4 pb-5 border-b border-[#584738]/5 items-start">
                  <div className="w-16 h-16 rounded-xs bg-gray-50 overflow-hidden flex-shrink-0 border border-[#584738]/10">
                    <img src={item.image} alt={item.name} onError={(e) => { e.target.onerror = null; e.target.style.opacity = '0'; }} className="w-full h-full object-cover" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h4 className="font-serif text-[13.5px] font-semibold text-[#584738] truncate">{item.name}</h4>
                    <p className="text-[12.5px] text-[#584738] font-medium mt-1">
                      ₹{item.price.toLocaleString("en-IN")} <span className="text-[9.5px] text-[#584738]/60 font-sans font-normal">(incl. of all taxes)</span>
                    </p>
                    
                    <div className="flex items-center justify-between text-xs mt-2 font-sans">
                      <div className="flex items-center border border-[#584738]/20 select-none">
                        <button onClick={() => handleQtyChange(item.id, -1)} className="px-2 py-0.5 hover:bg-[#584738]/5 focus:outline-none">-</button>
                        <span className="px-3 py-0.5 text-center font-medium bg-[#584738]/2.5">{item.qty}</span>
                        <button onClick={() => handleQtyChange(item.id, 1)} className="px-2 py-0.5 hover:bg-[#584738]/5 focus:outline-none">+</button>
                      </div>

                      <button onClick={() => removeItem(item.id)} className="text-[#584738]/60 hover:text-red-500 font-semibold">Remove</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {cartItems.length > 0 && (
            <div className="border-t border-[#584738]/10 pt-6 mt-6 flex flex-col gap-4 flex-shrink-0">
              <div className="flex items-center justify-between font-serif">
                <div className="flex flex-col text-left">
                  <span className="text-md font-medium tracking-wide text-[#584738]/80">Subtotal</span>
                  <span className="text-[10px] text-[#584738]/60 font-sans normal-case">(Inclusive of all taxes)</span>
                </div>
                <span className="text-lg font-bold text-[#584738]">₹{getSubtotal().toLocaleString("en-IN")}</span>
              </div>
              <p className="text-[10px] text-[#584738]/60 tracking-wider text-center font-sans">
                Complimentary Express Shipping • (Inclusive of all taxes)
              </p>
              <button
                onClick={() => {
                  setView('checkout');
                  setIsCartOpen(false);
                }}
                className="w-full bg-[#584738] text-white hover:bg-black py-3.5 text-xs tracking-widest font-bold uppercase transition-colors shadow-xs font-sans"
              >
                PROCEED TO CHECKOUT
              </button>
            </div>
          )}

          {/* UNDO TOAST */}
          {undoItem && (
            <div className="flex-shrink-0 mt-3 flex items-center justify-between gap-3 bg-[#584738] text-[#F5E6D8] px-4 py-3 rounded-xs animate-fade-in">
              <p className="text-[11.5px] font-sans tracking-wide truncate flex-1">
                <span className="opacity-60 mr-1">Removed:</span>{undoItem.name}
              </p>
              <button
                onClick={handleUndo}
                className="flex items-center gap-1.5 text-[11px] font-sans font-bold tracking-widest uppercase text-[#CEC1A8] hover:text-white transition-colors focus:outline-none flex-shrink-0"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                </svg>
                Undo
              </button>
            </div>
          )}
        </div>
      </div>

      <BookingDrawer
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        bookingStep={bookingStep}
        setBookingStep={setBookingStep}
        bookingForm={bookingForm}
        setBookingForm={setBookingForm}
        cartItems={cartItems}
      />
      <AuthModal isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} currentUser={currentUser} setCurrentUser={setCurrentUser} />
    </div>
  );
}
